/** Preformatted text that fills its box: the largest font size at which all of it fits without scrolling. */

import { useLayoutEffect, useRef, useState } from "react";

interface FitTextProps {
  text: string;
  /** Sized as if there were at least this many lines, so a short text doesn't grow bigger than a fuller one. */
  minLines?: number;
  /** Smallest size to shrink to; below it the text scrolls instead. */
  minSize?: number;
  /** Largest size to grow to, so a one-line schedule doesn't become a billboard. */
  maxSize?: number;
  style?: React.CSSProperties;
}

export function FitText({ text, minLines = 0, minSize = 14, maxSize = 96, style }: FitTextProps) {
  const ref = useRef<HTMLPreElement>(null);
  const [size, setSize] = useState(minSize);
  // Blank (non-breaking-space) lines stand in for the missing ones; they only reserve height.
  const shown = text + '\n\u00a0'.repeat(Math.max(0, minLines - text.split('\n').length));

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const fits = (px: number) => {
      el.style.fontSize = `${px}px`;
      return el.scrollHeight <= el.clientHeight + 1 && el.scrollWidth <= el.clientWidth + 1;
    };
    const fit = () => {
      let lo = minSize, hi = maxSize;
      while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        if (fits(mid)) lo = mid; else hi = mid - 1;
      }
      el.style.fontSize = `${lo}px`;
      setSize(lo);
    };
    fit();
    // The box changes with the window (rotate, resize, browser zoom), not just with the text.
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => observer.disconnect();
  }, [shown, minSize, maxSize]);

  return (
    <pre ref={ref} style={{ ...style, fontSize: size }}>
      {shown}
    </pre>
  );
}
